import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import { API_URL, getPaymentProofUrl } from '../config';

const ProjectPayments = () => {
  const { projectId } = useParams();
  const { user: currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [beneficeNet, setBeneficeNet] = useState(0);
  const [totalDejaPaye, setTotalDejaPaye] = useState(0);
  const [totalEnAttenteApi, setTotalEnAttenteApi] = useState(0);
  const [activeTab, setActiveTab] = useState('payments'); // 'distributions' ou 'payments'
  const [showDistributionModal, setShowDistributionModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showProofModal, setShowProofModal] = useState(null); // pour uploader une preuve
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedDistribution, setSelectedDistribution] = useState(null);
  const [filters, setFilters] = useState({ statut: '', type_paiement: '' });
  const [paymentTarget, setPaymentTarget] = useState('collaborator'); // 'collaborator' ou 'platform'
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // États pour l'onglet Émissions
  const [distSearchTerm, setDistSearchTerm] = useState('');
  const [distCurrentPage, setDistCurrentPage] = useState(1);
  const [distStatusFilter, setDistStatusFilter] = useState('');
  const distItemsPerPage = 10;
  const [proofFile, setProofFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [paymentInstructions, setPaymentInstructions] = useState('');
  const [distributionData, setDistributionData] = useState({
    montant: '',
    description: ''
  });
  const [formData, setFormData] = useState({
    collaborator_id: '',
    user_id: '',
    type_paiement: 'salaire',
    montant: '',
    periode_debut: '',
    periode_fin: '',
    description: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      if (filters.statut) params.append('statut', filters.statut);
      if (filters.type_paiement) params.append('type_paiement', filters.type_paiement);
      
      const [paymentsRes, collabsRes, instructionsRes, distributionsRes] = await Promise.all([
        axios.get(`${API_URL}/project-finances/${projectId}/payments?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/project-finances/${projectId}/collaborators`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/services/payment-instructions`),
        axios.get(`${API_URL}/project-finances/${projectId}/distributions`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setPayments(paymentsRes.data.payments || []);
      setCanManage(paymentsRes.data.can_manage || false);
      setBeneficeNet(paymentsRes.data.benefice_net || 0);
      setTotalDejaPaye(paymentsRes.data.total_deja_paye || 0);
      setTotalEnAttenteApi(paymentsRes.data.total_en_attente || 0);
      setCollaborators(collabsRes.data.collaborators || []);
      setPaymentInstructions(instructionsRes.data.payment_instructions || '');
      setDistributions(distributionsRes.data.distributions || []);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Si l'utilisateur n'est pas manager, forcer l'onglet 'payments'
  // Si c'est le premier chargement et qu'il est manager, mettre 'distributions'
  const [initialTabSet, setInitialTabSet] = useState(false);
  useEffect(() => {
    if (!canManage && activeTab === 'distributions') {
      setActiveTab('payments');
    }
    // Au premier chargement, si manager, mettre sur distributions
    if (canManage && !initialTabSet) {
      setActiveTab('distributions');
      setInitialTabSet(true);
    }
  }, [canManage, activeTab, initialTabSet]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      // Préparer les données selon le destinataire
      const paymentPayload = {
        ...formData,
        is_platform_payment: paymentTarget === 'platform'
      };
      
      // Pour paiement plateforme, on n'envoie pas de collaborator_id numérique
      if (paymentTarget === 'platform') {
        paymentPayload.collaborator_id = null;
        paymentPayload.user_id = null;
        paymentPayload.type_paiement = 'plateforme';
      }
      
      await axios.post(
        `${API_URL}/project-finances/${projectId}/payments`,
        paymentPayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  // Confirmer un paiement (par l'auteur/admin après vérification de la preuve)
  const handleMarkAsPaid = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/project-finances/${projectId}/payments/${selectedPayment.id}`,
        { statut: 'paye' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowPayModal(false);
      setSelectedPayment(null);
      fetchData();
      alert('✅ Paiement confirmé avec succès !');
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  // Créer une émission de partage (100% du bénéfice net automatiquement)
  const handleCreateDistribution = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/project-finances/${projectId}/distributions`,
        { 
          montant: beneficeNet, // Toujours 100% du bénéfice net
          description: distributionData.description 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`✅ Émission créée avec succès !\nRéférence: ${res.data.reference}\nMontant: ${formatMoney(res.data.montant_total)}`);
      setShowDistributionModal(false);
      setDistributionData({ montant: '', description: '' });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || err.response?.data?.message || 'Erreur');
    }
  };

  // Annuler une émission
  const handleCancelDistribution = async (distributionId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette émission ? Cette action supprimera la dépense associée et annulera tous les paiements non encore effectués.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/project-finances/${projectId}/distributions/${distributionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Émission annulée avec succès');
      setSelectedDistribution(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const resetForm = () => {
    setPaymentTarget('collaborator');
    setFormData({
      collaborator_id: '',
      user_id: '',
      type_paiement: 'salaire',
      montant: '',
      periode_debut: '',
      periode_fin: '',
      description: ''
    });
  };

  const handleUploadProof = async (paymentId) => {
    if (!proofFile) {
      alert('Veuillez sélectionner un fichier');
      return;
    }
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('proof', proofFile);
      
      await axios.post(
        `${API_URL}/project-finances/${projectId}/payments/${paymentId}/proof`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      
      alert('Preuve de paiement uploadée avec succès ! Le destinataire peut maintenant confirmer.');
      setShowProofModal(null);
      setProofFile(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const typeLabels = {
    salaire: { label: 'Salaire', color: 'bg-blue-100 text-blue-700', icon: '💰' },
    benefices: { label: 'Part des bénéfices', color: 'bg-green-100 text-green-700', icon: '📈' },
    bonus: { label: 'Bonus', color: 'bg-purple-100 text-purple-700', icon: '🎁' },
    remboursement: { label: 'Remboursement', color: 'bg-orange-100 text-orange-700', icon: '↩️' },
    plateforme: { label: 'Commission Plateforme', color: 'bg-teal-100 text-teal-700', icon: '🏢' },
    autre: { label: 'Autre', color: 'bg-gray-100 text-gray-700', icon: '📋' }
  };

  const statusLabels = {
    en_attente: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700', icon: '⏳' },
    valide: { label: 'Validé', color: 'bg-blue-100 text-blue-700', icon: '✓' },
    paye: { label: 'Payé', color: 'bg-green-100 text-green-700', icon: '✅' },
    annule: { label: 'Annulé', color: 'bg-red-100 text-red-700', icon: '❌' }
  };

  // Filtrer les paiements: collaborateurs ne voient que leurs paiements, auteur/admin voient tout
  const filteredPayments = canManage 
    ? payments 
    : payments.filter(p => String(p.user_id) === String(currentUser?.id));

  // Appliquer la recherche
  const searchedPayments = filteredPayments.filter(p => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (p.nom && p.nom.toLowerCase().includes(search)) ||
      (p.prenom && p.prenom.toLowerCase().includes(search)) ||
      (p.email && p.email.toLowerCase().includes(search)) ||
      (p.description && p.description.toLowerCase().includes(search)) ||
      (p.type_paiement && p.type_paiement.toLowerCase().includes(search)) ||
      (p.montant && String(p.montant).includes(search)) ||
      (p.reference && p.reference.toLowerCase().includes(search))
    );
  });

  // Pagination
  const totalPages = Math.ceil(searchedPayments.length / itemsPerPage);
  const paginatedPayments = searchedPayments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset page quand recherche change
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  // Filtrage et pagination des émissions
  const filteredDistributions = distributions.filter(dist => {
    // Filtre par statut
    if (distStatusFilter) {
      const isComplete = dist.nb_paye === dist.nb_total;
      const isCancelled = dist.statut === 'annulee';
      if (distStatusFilter === 'complete' && !isComplete) return false;
      if (distStatusFilter === 'en_cours' && (isComplete || isCancelled)) return false;
      if (distStatusFilter === 'annulee' && !isCancelled) return false;
    }
    // Filtre par recherche
    if (!distSearchTerm) return true;
    const search = distSearchTerm.toLowerCase();
    return (
      (dist.reference && dist.reference.toLowerCase().includes(search)) ||
      (dist.created_by_nom && dist.created_by_nom.toLowerCase().includes(search)) ||
      (dist.created_by_prenom && dist.created_by_prenom.toLowerCase().includes(search)) ||
      (dist.description && dist.description.toLowerCase().includes(search)) ||
      (dist.montant_total && String(dist.montant_total).includes(search))
    );
  });

  const distTotalPages = Math.ceil(filteredDistributions.length / distItemsPerPage);
  const paginatedDistributions = filteredDistributions.slice(
    (distCurrentPage - 1) * distItemsPerPage,
    distCurrentPage * distItemsPerPage
  );

  const handleDistSearchChange = (value) => {
    setDistSearchTerm(value);
    setDistCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <Link to={`/projects/${projectId}/finances`} className="text-indigo-200 hover:text-white text-sm mb-2 inline-flex items-center gap-1">
            ← Retour au dashboard
          </Link>
          <h1 className="text-3xl font-bold">💳 Paiements aux collaborateurs</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          <Link to={`/projects/${projectId}/finances`} className="px-4 py-2 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 shadow-sm">
            📊 Dashboard
          </Link>
          <Link to={`/projects/${projectId}/finances/collaborators`} className="px-4 py-2 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 shadow-sm">
            👥 Collaborateurs
          </Link>
          <Link to={`/projects/${projectId}/finances/payments`} className="px-4 py-2 bg-indigo-600 text-white rounded-xl font-medium">
            💳 Paiements
          </Link>
          <Link to={`/projects/${projectId}/finances/reports`} className="px-4 py-2 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 shadow-sm">
            📈 Bilans
          </Link>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-blue-500">
            <p className="text-gray-500 text-sm">Bénéfice net total</p>
            <p className={`text-2xl font-bold ${beneficeNet >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
              {formatMoney(beneficeNet)}
            </p>
            {beneficeNet > 1000 && <p className="text-xs text-green-500 mt-1">✓ Peut distribuer</p>}
            {beneficeNet <= 1000 && <p className="text-xs text-red-500 mt-1">✗ Insuffisant (&lt;1000)</p>}
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-green-500">
            <p className="text-gray-500 text-sm">Total distribué</p>
            <p className="text-2xl font-bold text-green-600">{formatMoney(totalDejaPaye)}</p>
            <p className="text-xs text-gray-400 mt-1">Paiements confirmés</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-yellow-500">
            <p className="text-gray-500 text-sm">En attente</p>
            <p className="text-2xl font-bold text-yellow-600">{formatMoney(totalEnAttenteApi)}</p>
            {totalEnAttenteApi > 0 && <p className="text-xs text-yellow-600 mt-1">⏳ À confirmer</p>}
            {totalEnAttenteApi <= 0 && <p className="text-xs text-green-500 mt-1">✓ Aucun en attente</p>}
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-purple-500">
            <p className="text-gray-500 text-sm">Statut émission</p>
            {beneficeNet > 1000 && totalEnAttenteApi <= 0 ? (
              <p className="text-lg font-bold text-green-600">✅ Peut créer</p>
            ) : (
              <p className="text-lg font-bold text-red-600">❌ Bloqué</p>
            )}
            <p className="text-xs text-gray-400 mt-1">
              {beneficeNet <= 1000 ? 'Bénéfice < 1000' : totalEnAttenteApi > 0 ? 'Paiements en attente' : ''}
            </p>
          </div>
        </div>

        {/* Alerte si conditions non remplies pour créer une émission */}
        {canManage && totalEnAttenteApi > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⏳</span>
              <div>
                <h4 className="font-semibold text-yellow-800">Paiements en attente de confirmation</h4>
                <p className="text-sm text-yellow-700">
                  Vous avez {formatMoney(totalEnAttenteApi)} de paiements en attente. 
                  Confirmez-les avant de pouvoir créer une nouvelle émission de partage.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Bouton créer émission + Tabs */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div className="flex gap-2">
              {/* Onglet Émissions visible uniquement pour auteur/admin */}
              {canManage && (
                <button
                  onClick={() => setActiveTab('distributions')}
                  className={`px-4 py-2 rounded-xl font-medium transition ${
                    activeTab === 'distributions' 
                      ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  📦 Émissions de partage
                </button>
              )}
              <button
                onClick={() => setActiveTab('payments')}
                className={`px-4 py-2 rounded-xl font-medium transition ${
                  activeTab === 'payments' 
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                💳 Tous les paiements
              </button>
            </div>
            
            {canManage && beneficeNet > 1000 && totalEnAttenteApi <= 0 && (
              <button
                onClick={() => setShowDistributionModal(true)}
                className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-medium hover:shadow-lg transition-all flex items-center gap-2"
              >
                <span>💰</span>
                <span>Nouvelle émission de partage</span>
              </button>
            )}
          </div>
          
          {/* Explication du système */}
          {activeTab === 'distributions' && (
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-5 text-sm border border-indigo-100">
              <p className="font-bold text-indigo-800 mb-3">💡 Comment fonctionne le partage des bénéfices ?</p>
              <div className="grid md:grid-cols-4 gap-4 text-indigo-700">
                <div className="flex gap-2">
                  <span className="text-2xl">1️⃣</span>
                  <div>
                    <p className="font-semibold">Créer l'émission</p>
                    <p className="text-xs text-indigo-600">Le système calcule automatiquement la part de chacun (collaborateurs + plateforme 3%)</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <span className="text-2xl">2️⃣</span>
                  <div>
                    <p className="font-semibold">Effectuer les paiements</p>
                    <p className="text-xs text-indigo-600">Payez chaque bénéficiaire via Mobile Money, virement, etc.</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <span className="text-2xl">3️⃣</span>
                  <div>
                    <p className="font-semibold">Uploader les preuves</p>
                    <p className="text-xs text-indigo-600">Dans l'onglet "Tous les paiements", uploadez le reçu/capture d'écran</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <span className="text-2xl">4️⃣</span>
                  <div>
                    <p className="font-semibold">Confirmation</p>
                    <p className="text-xs text-indigo-600">Les collaborateurs reçoivent une notification et confirment la réception</p>
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-indigo-200">
                <p className="text-xs text-indigo-600 flex items-center gap-2">
                  <span className="text-lg">👆</span>
                  <span><strong>Cliquez sur une émission</strong> ci-dessous pour voir les détails et le statut de chaque paiement</span>
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Contenu selon l'onglet */}
        {activeTab === 'distributions' ? (
          /* Liste des émissions */
          <div className="space-y-4">
            {/* Barre de recherche et filtres pour émissions */}
            <div className="bg-white rounded-2xl shadow-sm p-4">
              <div className="flex flex-wrap items-center gap-4">
                {/* Barre de recherche */}
                <div className="relative flex-1 min-w-[200px]">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
                  <input
                    type="text"
                    value={distSearchTerm}
                    onChange={(e) => handleDistSearchChange(e.target.value)}
                    placeholder="Rechercher par référence, créateur, montant..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  {distSearchTerm && (
                    <button
                      onClick={() => handleDistSearchChange('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  )}
                </div>
                
                {/* Filtre par statut */}
                <select
                  value={distStatusFilter}
                  onChange={(e) => { setDistStatusFilter(e.target.value); setDistCurrentPage(1); }}
                  className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Tous les statuts</option>
                  <option value="en_cours">🟡 En cours</option>
                  <option value="complete">✅ Complétées</option>
                  <option value="annulee">❌ Annulées</option>
                </select>
              </div>
              
              {/* Info résultats */}
              {(distSearchTerm || distStatusFilter) && (
                <div className="mt-3 text-sm text-gray-500">
                  {filteredDistributions.length} émission(s) trouvée(s)
                  {distSearchTerm && <span className="ml-2">pour "{distSearchTerm}"</span>}
                </div>
              )}
            </div>
            
            {filteredDistributions.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                <span className="text-6xl">📦</span>
                <h3 className="text-xl font-bold text-gray-800 mt-4">
                  {distSearchTerm || distStatusFilter ? 'Aucun résultat' : 'Aucune émission de partage'}
                </h3>
                <p className="text-gray-500 mt-2">
                  {distSearchTerm || distStatusFilter 
                    ? 'Essayez avec d\'autres critères de recherche'
                    : 'Créez une émission pour distribuer les bénéfices aux collaborateurs'
                  }
                </p>
                {!distSearchTerm && !distStatusFilter && canManage && beneficeNet > 1000 && totalEnAttenteApi <= 0 && (
                  <button
                    onClick={() => setShowDistributionModal(true)}
                    className="mt-4 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-medium hover:shadow-lg transition"
                  >
                    💰 Créer une émission
                  </button>
                )}
              </div>
            ) : (
              <>
              {paginatedDistributions.map((dist) => (
                <div 
                  key={dist.id} 
                  className={`bg-white rounded-2xl shadow-sm overflow-hidden border-2 transition-all ${
                    dist.statut === 'annulee' ? 'opacity-60 border-gray-200' : 
                    selectedDistribution?.id === dist.id ? 'border-purple-400 shadow-lg' : 'border-transparent hover:border-purple-200'
                  }`}
                >
                  <div 
                    className="p-4 cursor-pointer hover:bg-purple-50/50 transition-colors"
                    onClick={() => setSelectedDistribution(selectedDistribution?.id === dist.id ? null : dist)}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white text-xl ${
                          dist.statut === 'annulee' ? 'bg-gray-400' : 
                          dist.nb_paye === dist.nb_total ? 'bg-green-500' : 'bg-purple-500'
                        }`}>
                          {dist.statut === 'annulee' ? '❌' : dist.nb_paye === dist.nb_total ? '✅' : '📦'}
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-800 flex items-center gap-2">
                            {dist.reference}
                            <span className={`text-xs transition-transform duration-200 ${
                              selectedDistribution?.id === dist.id ? 'rotate-180' : ''
                            }`}>▼</span>
                          </h4>
                          <p className="text-sm text-gray-500">{formatDate(dist.created_at)} • Par {dist.created_by_prenom} {dist.created_by_nom}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-xl font-bold text-purple-600">{formatMoney(dist.montant_total)}</p>
                          <p className="text-sm text-gray-500">
                            {dist.statut === 'annulee' ? (
                              <span className="text-red-600">Annulée</span>
                            ) : (
                              <span className={dist.nb_paye === dist.nb_total ? 'text-green-600' : 'text-yellow-600'}>
                                {dist.nb_paye}/{dist.nb_total} confirmés
                              </span>
                            )}
                          </p>
                        </div>
                        {dist.statut !== 'annulee' && dist.nb_paye < dist.nb_total && (
                          <div className="hidden sm:flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium animate-pulse">
                            <span>⏳</span>
                            <span>Action requise</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Indication pour cliquer */}
                    {selectedDistribution?.id !== dist.id && dist.statut !== 'annulee' && (
                      <p className="text-xs text-purple-500 mt-2 flex items-center gap-1">
                        <span>👆</span>
                        <span>Cliquez pour voir les détails et gérer les paiements</span>
                      </p>
                    )}
                  </div>
                  
                  {/* Détails de l'émission */}
                  {selectedDistribution?.id === dist.id && (
                    <div className="border-t bg-gray-50 p-4">
                      {/* Instructions d'action si des paiements sont en attente */}
                      {dist.statut !== 'annulee' && dist.nb_paye < dist.nb_total && canManage && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
                          <p className="font-semibold text-yellow-800 flex items-center gap-2 mb-2">
                            <span className="text-xl">⚡</span>
                            Prochaines étapes pour finaliser cette émission :
                          </p>
                          <ol className="text-sm text-yellow-700 space-y-2 ml-7 list-decimal">
                            <li>Effectuez les paiements ci-dessous via Mobile Money, virement ou autre</li>
                            <li>Allez dans l'onglet <strong>"💳 Tous les paiements"</strong> pour uploader les preuves (reçu, capture d'écran)</li>
                            <li>Les collaborateurs recevront une notification et pourront <strong>confirmer la réception</strong></li>
                          </ol>
                        </div>
                      )}
                      
                      <h5 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <span>📋</span>
                        Répartition des paiements
                        <span className="text-xs font-normal text-gray-500">({dist.payments?.length || 0} bénéficiaires)</span>
                      </h5>
                      <div className="space-y-2">
                        {dist.payments?.map((payment) => (
                          <div 
                            key={payment.id} 
                            className={`flex items-center justify-between p-3 rounded-xl transition-colors ${
                              payment.statut === 'en_attente' ? 'bg-yellow-50 border border-yellow-200' : 
                              payment.statut === 'paye' ? 'bg-green-50 border border-green-200' : 'bg-white'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-xl">
                                {payment.type_paiement === 'plateforme' ? '🏢' : '👤'}
                              </span>
                              <div>
                                <p className="font-medium text-gray-800">
                                  {payment.type_paiement === 'plateforme' 
                                    ? 'Plateforme DJAMANN (3%)' 
                                    : `${payment.prenom || ''} ${payment.nom || 'Utilisateur'}`
                                  }
                                </p>
                                <p className="text-xs text-gray-500">{payment.description}</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                              <span className="font-bold text-gray-800">{formatMoney(payment.montant)}</span>
                              <span className={`px-2 py-1 rounded-full text-xs ${statusLabels[payment.statut]?.color || 'bg-gray-100'}`}>
                                {statusLabels[payment.statut]?.icon} {statusLabels[payment.statut]?.label}
                              </span>
                              {canManage && payment.statut === 'en_attente' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Aller à l'onglet "Tous les paiements" et filtrer par ce paiement
                                    setActiveTab('payments');
                                    setSearchTerm(payment.type_paiement === 'plateforme' ? 'plateforme' : (payment.nom || ''));
                                  }}
                                  className="px-3 py-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg text-sm hover:shadow-md transition flex items-center gap-1"
                                >
                                  📤 Uploader preuve
                                </button>
                              )}
                              {payment.preuve_paiement && (
                                <a 
                                  href={getPaymentProofUrl(payment.preuve_paiement)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 text-sm hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  📎 Voir preuve
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Actions sur l'émission */}
                      {canManage && dist.statut !== 'annulee' && dist.nb_paye === 0 && (
                        <div className="mt-4 pt-4 border-t flex justify-end">
                          <button
                            onClick={() => handleCancelDistribution(dist.id)}
                            className="px-4 py-2 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition"
                          >
                            ❌ Annuler cette émission
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              
              {/* Pagination des émissions */}
              {distTotalPages > 1 && (
                <div className="bg-white rounded-2xl shadow-sm p-4 mt-4 flex flex-wrap items-center justify-between gap-4">
                  <div className="text-sm text-gray-500">
                    Affichage {((distCurrentPage - 1) * distItemsPerPage) + 1} - {Math.min(distCurrentPage * distItemsPerPage, filteredDistributions.length)} sur {filteredDistributions.length}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setDistCurrentPage(1)}
                      disabled={distCurrentPage === 1}
                      className="px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ⏮️
                    </button>
                    <button
                      onClick={() => setDistCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={distCurrentPage === 1}
                      className="px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ◀️
                    </button>
                    
                    <div className="flex items-center gap-1">
                      {[...Array(distTotalPages)].map((_, i) => {
                        const page = i + 1;
                        if (page === 1 || page === distTotalPages || (page >= distCurrentPage - 1 && page <= distCurrentPage + 1)) {
                          return (
                            <button
                              key={page}
                              onClick={() => setDistCurrentPage(page)}
                              className={`px-3 py-2 rounded-lg ${
                                distCurrentPage === page
                                  ? 'bg-purple-600 text-white'
                                  : 'border border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        } else if (page === distCurrentPage - 2 || page === distCurrentPage + 2) {
                          return <span key={page} className="px-2">...</span>;
                        }
                        return null;
                      })}
                    </div>
                    
                    <button
                      onClick={() => setDistCurrentPage(prev => Math.min(prev + 1, distTotalPages))}
                      disabled={distCurrentPage === distTotalPages}
                      className="px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ▶️
                    </button>
                    <button
                      onClick={() => setDistCurrentPage(distTotalPages)}
                      disabled={distCurrentPage === distTotalPages}
                      className="px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ⏭️
                    </button>
                  </div>
                </div>
              )}
              </>
            )}
          </div>
        ) : (
          /* Liste de tous les paiements (ancien affichage) */
          <>
            {/* Filters + Search */}
            <div className="bg-white rounded-2xl shadow-sm p-4 mb-6">
              <div className="flex flex-wrap items-center gap-4">
                {/* Barre de recherche */}
                <div className="relative flex-1 min-w-[200px]">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder="Rechercher par nom, email, montant..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => handleSearchChange('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  )}
                </div>
                
                <select
                  value={filters.statut}
                  onChange={(e) => { setFilters(prev => ({ ...prev, statut: e.target.value })); setCurrentPage(1); }}
                  className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Tous les statuts</option>
                  {Object.entries(statusLabels).map(([key, val]) => (
                    <option key={key} value={key}>{val.icon} {val.label}</option>
                  ))}
                </select>
                
                <select
                  value={filters.type_paiement}
                  onChange={(e) => { setFilters(prev => ({ ...prev, type_paiement: e.target.value })); setCurrentPage(1); }}
                  className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Tous les types</option>
                  {Object.entries(typeLabels).map(([key, val]) => (
                    <option key={key} value={key}>{val.icon} {val.label}</option>
                  ))}
                </select>
              </div>
              
              {/* Info résultats */}
              {(searchTerm || filters.statut || filters.type_paiement) && (
                <div className="mt-3 text-sm text-gray-500">
                  {searchedPayments.length} résultat(s) trouvé(s)
                  {searchTerm && <span className="ml-2">pour "{searchTerm}"</span>}
                </div>
              )}
            </div>

            {/* Payments List */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
                </div>
              ) : paginatedPayments.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {paginatedPayments.map((payment) => {
                    const isPlatformPayment = payment.type_paiement === 'plateforme' || payment.is_platform_payment;
                    
                    return (
                      <div key={payment.id} className={`p-6 hover:bg-gray-50 transition-colors ${isPlatformPayment ? 'bg-teal-50/50 border-l-4 border-teal-500' : ''}`}>
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div className="flex items-center gap-4">
                            {isPlatformPayment ? (
                              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex items-center justify-center text-white text-xl">
                                🏢
                              </div>
                            ) : (
                              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center text-white text-lg font-bold">
                                {payment.nom?.[0]?.toUpperCase() || 'U'}
                              </div>
                            )}
                            <div>
                              <h3 className="font-bold text-gray-900">
                                {isPlatformPayment ? 'Plateforme DJAMANN' : `${payment.nom || 'Collaborateur'}`}
                              </h3>
                              <p className="text-sm text-gray-500">
                                {isPlatformPayment ? 'Commission 3%' : payment.email}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${typeLabels[payment.type_paiement]?.color || 'bg-gray-100 text-gray-700'}`}>
                              {typeLabels[payment.type_paiement]?.icon || '📋'} {typeLabels[payment.type_paiement]?.label || payment.type_paiement}
                            </span>
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${statusLabels[payment.statut]?.color}`}>
                              {statusLabels[payment.statut]?.icon} {statusLabels[payment.statut]?.label}
                            </span>
                          </div>
                        </div>

                        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <p className="text-xs text-gray-500">Montant</p>
                            <p className={`text-xl font-bold ${isPlatformPayment ? 'text-teal-600' : 'text-indigo-600'}`}>{formatMoney(payment.montant)}</p>
                          </div>
                          {payment.periode_debut && (
                            <div>
                              <p className="text-xs text-gray-500">Période</p>
                              <p className="text-sm text-gray-800">{formatDate(payment.periode_debut)} → {formatDate(payment.periode_fin)}</p>
                            </div>
                          )}
                          {payment.date_paiement && (
                            <div>
                              <p className="text-xs text-gray-500">Payé le</p>
                              <p className="text-sm text-gray-800">{formatDate(payment.date_paiement)}</p>
                            </div>
                          )}
                          {payment.methode_paiement && (
                            <div>
                              <p className="text-xs text-gray-500">Méthode</p>
                              <p className="text-sm text-gray-800">{payment.methode_paiement}</p>
                            </div>
                          )}
                        </div>

                        {payment.description && (
                          <p className="mt-3 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">{payment.description}</p>
                        )}

                        {/* Afficher la preuve de paiement si existante */}
                        {payment.preuve_paiement && (
                          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="text-sm text-blue-800 font-medium mb-2">📎 Preuve de paiement :</p>
                            <a
                              href={getPaymentProofUrl(payment.preuve_paiement)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
                            >
                              {payment.preuve_paiement.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                <img
                                  src={getPaymentProofUrl(payment.preuve_paiement)}
                                  alt="Preuve"
                                  className="w-24 h-24 object-cover rounded-lg border border-blue-200"
                                />
                              ) : (
                                <>📄 Voir le justificatif</>
                              )}
                            </a>
                          </div>
                        )}

                        {(payment.statut === 'en_attente' || payment.statut === 'valide') && (
                          <div className="mt-4 flex flex-wrap gap-2">
                            {/* Manager peut uploader une preuve */}
                            {canManage && !payment.preuve_paiement && (
                              <button
                                onClick={() => setShowProofModal(payment)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                              >
                                📤 Uploader preuve de paiement
                              </button>
                            )}
                            
                            {/* SEUL le destinataire peut confirmer (celui qui reçoit l'argent) */}
                            {/* Pour paiements plateforme: seul un admin global peut confirmer */}
                            {currentUser && (
                              (!isPlatformPayment && String(payment.user_id) === String(currentUser.id)) || 
                              (isPlatformPayment && currentUser.role === 'admin')
                            ) && (
                              <button
                                onClick={() => {
                                  if (!payment.preuve_paiement) {
                                    alert('Impossible de confirmer : aucune preuve de paiement n\'a été uploadée par le payeur.');
                                    return;
                                  }
                                  setSelectedPayment(payment);
                                  setShowPayModal(true);
                                }}
                                disabled={!payment.preuve_paiement}
                                className={`px-4 py-2 ${
                                  payment.preuve_paiement
                                    ? isPlatformPayment ? 'bg-teal-600 hover:bg-teal-700' : 'bg-green-600 hover:bg-green-700'
                                    : 'bg-gray-300 cursor-not-allowed'
                                } text-white rounded-lg text-sm font-medium transition-colors`}
                                title={!payment.preuve_paiement ? 'En attente de preuve de paiement' : ''}
                              >
                                {payment.preuve_paiement ? '✅ Confirmer réception' : '⏳ En attente de preuve'}
                              </button>
                            )}
                            {/* L'admin/auteur peut annuler */}
                            {canManage && (
                              <button
                                onClick={async () => {
                                  if (!window.confirm('Annuler ce paiement ?')) return;
                                  const token = localStorage.getItem('token');
                                  await axios.put(
                                    `${API_URL}/project-finances/${projectId}/payments/${payment.id}`,
                                    { statut: 'annule' },
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  fetchData();
                                }}
                                className="px-4 py-2 bg-red-100 text-red-600 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors"
                              >
                                Annuler
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-12 text-center text-gray-500">
                  <span className="text-5xl">💳</span>
                  <p className="mt-4 text-lg">Aucun paiement</p>
                  {canManage && (
                    <button
                      onClick={() => setShowModal(true)}
                      className="mt-4 px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors"
                    >
                      Créer un paiement
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white rounded-2xl shadow-sm p-4 mt-4 flex flex-wrap items-center justify-between gap-4">
                <div className="text-sm text-gray-500">
                  Affichage {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, searchedPayments.length)} sur {searchedPayments.length}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ⏮️
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ◀️
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {[...Array(totalPages)].map((_, i) => {
                      const page = i + 1;
                      // Afficher seulement quelques pages autour de la page actuelle
                      if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-2 rounded-lg ${
                              currentPage === page
                                ? 'bg-indigo-600 text-white'
                                : 'border border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} className="px-2">...</span>;
                      }
                      return null;
                    })}
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ▶️
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ⏭️
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Payment Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg my-8 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-xl font-bold text-gray-900">➕ Nouveau paiement</h3>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Choix: Collaborateur ou Plateforme */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Destinataire</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setPaymentTarget('collaborator');
                      setFormData(prev => ({ ...prev, type_paiement: 'salaire' }));
                    }}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      paymentTarget === 'collaborator'
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-2xl">👤</span>
                    <p className="font-medium mt-1">Collaborateur</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setPaymentTarget('platform');
                      // Calculer automatiquement 3% du bénéfice net
                      const platformAmount = beneficeNet > 0 ? Math.round(beneficeNet * 0.03) : 0;
                      setFormData(prev => ({ 
                        ...prev, 
                        type_paiement: 'plateforme',
                        collaborator_id: 'platform',
                        user_id: '',
                        montant: platformAmount
                      }));
                    }}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      paymentTarget === 'platform'
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-2xl">🏢</span>
                    <p className="font-medium mt-1">Plateforme DJAMANN</p>
                    <p className="text-xs text-teal-600">Commission 3% = {formatMoney(beneficeNet > 0 ? Math.round(beneficeNet * 0.03) : 0)}</p>
                  </button>
                </div>
              </div>

              {/* Collaborator - seulement si destinataire = collaborateur */}
              {paymentTarget === 'collaborator' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Collaborateur *</label>
                  <select
                    value={formData.collaborator_id}
                    onChange={(e) => {
                      const collabId = e.target.value;
                      // Pour l'auteur (id null), chercher par user_id
                      const collab = collaborators.find(c => 
                        (c.id && String(c.id) === collabId) || 
                        (!c.id && c.is_author && collabId === `author_${c.user_id}`)
                      );
                      
                      // Calculer le montant automatiquement selon la part des bénéfices
                      let calculatedAmount = '';
                      if (collab && collab.pourcentage_benefices > 0 && beneficeNet > 0) {
                        calculatedAmount = Math.round((collab.pourcentage_benefices * beneficeNet) / 100);
                      }
                      
                      setFormData(prev => ({
                        ...prev,
                        collaborator_id: collabId,
                        user_id: collab?.user_id || '',
                        montant: calculatedAmount || prev.montant,
                        type_paiement: calculatedAmount ? 'benefices' : prev.type_paiement
                      }));
                    }}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="">Sélectionner...</option>
                    {collaborators.map(c => {
                      const key = c.id || `author_${c.user_id}`;
                      const pourcentage = c.pourcentage_benefices || 0;
                      const montantEstime = beneficeNet > 0 ? Math.round((pourcentage * beneficeNet) / 100) : 0;
                      return (
                        <option key={key} value={key}>
                          {c.nom} - {c.role} ({pourcentage}% = {formatMoney(montantEstime)})
                        </option>
                      );
                    })}
                  </select>
                  
                  {/* Info sur le calcul automatique */}
                  {formData.collaborator_id && beneficeNet > 0 && (
                    <div className="mt-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <p className="text-purple-800 text-sm">
                        💡 <strong>Calcul automatique :</strong> Le montant est pré-rempli selon la part des bénéfices du collaborateur.
                        Bénéfice net : {formatMoney(beneficeNet)}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Info plateforme */}
              {paymentTarget === 'platform' && (
                <div className="space-y-3">
                  <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                    <p className="text-teal-800 text-sm">
                      <span className="font-bold">🏢 Paiement Plateforme</span><br/>
                      Ce paiement représente la commission de 3% due à DJAMANN pour les services de la plateforme.
                    </p>
                  </div>
                  
                  {/* Instructions de paiement */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-amber-800 font-medium mb-2">📋 Comment effectuer le paiement ?</p>
                    <div className="text-amber-700 text-sm whitespace-pre-line">
                      {paymentInstructions || "📱 Effectuez le paiement via Mobile Money ou autre méthode, puis uploadez la preuve de paiement."}
                    </div>
                  </div>
                </div>
              )}

              {/* Type - seulement pour collaborateur */}
              {paymentTarget === 'collaborator' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type de paiement</label>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(typeLabels).filter(([key]) => key !== 'plateforme').slice(0, 4).map(([key, val]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, type_paiement: key }))}
                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                          formData.type_paiement === key 
                            ? 'border-indigo-500 bg-indigo-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="text-lg">{val.icon}</span>
                        <p className="font-medium text-sm mt-1">{val.label}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Montant */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Montant *</label>
                <div className="relative">
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={formData.montant}
                    onChange={(e) => {
                      // Ne pas permettre la modification pour les paiements plateforme
                      if (paymentTarget !== 'platform') {
                        setFormData(prev => ({ ...prev, montant: e.target.value }));
                      }
                    }}
                    readOnly={paymentTarget === 'platform'}
                    className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 text-lg font-semibold ${
                      paymentTarget === 'platform' ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                    placeholder="0"
                    required
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">XOF</span>
                </div>
                {paymentTarget === 'platform' && (
                  <p className="text-xs text-teal-600 mt-1">
                    💡 Montant calculé automatiquement : 3% du bénéfice net ({formatMoney(beneficeNet)})
                  </p>
                )}
              </div>

              {/* Période */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Période début</label>
                  <input
                    type="date"
                    value={formData.periode_debut}
                    onChange={(e) => setFormData(prev => ({ ...prev, periode_debut: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Période fin</label>
                  <input
                    type="date"
                    value={formData.periode_fin}
                    onChange={(e) => setFormData(prev => ({ ...prev, periode_fin: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 resize-none"
                  rows={2}
                  placeholder="Détails du paiement..."
                ></textarea>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition-all"
                >
                  Créer le paiement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mark as Paid Modal - Pour l'auteur/admin qui confirme après vérification de la preuve */}
      {showPayModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md my-8 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-green-500 to-emerald-600 rounded-t-2xl">
              <h3 className="text-xl font-bold text-white">✅ Confirmer la réception</h3>
              <p className="text-green-100 mt-1">
                {formatMoney(selectedPayment.montant)} → {selectedPayment.type_paiement === 'plateforme' ? 'Plateforme DJAMANN' : `${selectedPayment.prenom || ''} ${selectedPayment.nom || ''}`}
              </p>
            </div>
            
            {/* Afficher la preuve si disponible */}
            {selectedPayment.preuve_paiement && (
              <div className="p-4 bg-blue-50 border-b border-blue-100">
                <p className="text-sm text-blue-800 font-medium mb-2">📎 Preuve de paiement reçue :</p>
                <a 
                  href={getPaymentProofUrl(selectedPayment.preuve_paiement)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition"
                >
                  👁️ Voir la preuve
                </a>
              </div>
            )}
            
            <form onSubmit={handleMarkAsPaid} className="p-6 space-y-4">
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                En confirmant, vous attestez avoir vérifié la preuve de paiement et que le paiement a bien été effectué.
              </p>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowPayModal(false); setSelectedPayment(null); }}
                  className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-medium hover:shadow-lg transition-all"
                >
                  ✅ Confirmer le paiement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Proof Modal */}
      {showProofModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-xl font-bold text-gray-900">📤 Uploader une preuve de paiement</h3>
              <p className="text-gray-500 mt-1">
                {formatMoney(showProofModal.montant)} pour {showProofModal.nom || 'Plateforme'}
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Instructions de paiement */}
              {(showProofModal.type_paiement === 'plateforme' || showProofModal.is_platform_payment) && paymentInstructions && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-amber-800 font-medium mb-2">📋 Comment effectuer le paiement ?</p>
                  <div className="text-amber-700 text-sm whitespace-pre-line">
                    {paymentInstructions}
                  </div>
                </div>
              )}
              
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-blue-800 text-sm">
                  <strong>Important :</strong> Uploadez une capture d'écran ou un reçu de votre paiement. 
                  Le destinataire ne pourra confirmer la réception qu'après avoir vu cette preuve.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fichier (image ou PDF) *
                </label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setProofFile(e.target.files[0])}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500"
                />
                {proofFile && (
                  <p className="text-sm text-green-600 mt-2">✓ Fichier sélectionné : {proofFile.name}</p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => { setShowProofModal(null); setProofFile(null); }}
                  className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => handleUploadProof(showProofModal.id)}
                  disabled={!proofFile || uploading}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? '⏳ Envoi...' : '📤 Envoyer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Création Émission de Partage */}
      {showDistributionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg my-8">
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-green-500 to-emerald-600 rounded-t-2xl">
              <h3 className="text-xl font-bold text-white">💰 Nouvelle émission de partage</h3>
              <p className="text-green-100 text-sm mt-1">Distribuer la totalité du bénéfice net</p>
            </div>
            
            <form onSubmit={handleCreateDistribution} className="p-6 space-y-4">
              {/* Montant à distribuer (automatique = 100% du bénéfice) */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-5 text-center">
                <p className="text-purple-700 text-sm mb-1">Montant à distribuer</p>
                <p className="text-3xl font-bold text-purple-700">{formatMoney(beneficeNet)}</p>
                <p className="text-xs text-purple-500 mt-1">100% du bénéfice net</p>
              </div>

              {/* Aperçu de la répartition */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="font-medium text-gray-700 mb-3">📊 Répartition automatique :</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-1 border-b border-gray-200">
                    <span className="text-teal-700">🏢 Plateforme DJAMANN (3%)</span>
                    <span className="font-semibold">{formatMoney(beneficeNet * 0.03)}</span>
                  </div>
                  {collaborators.filter(c => parseFloat(c.pourcentage_benefices) > 0).map(c => (
                    <div key={c.id} className="flex justify-between py-1 border-b border-gray-100">
                      <span>👤 {c.prenom} {c.nom} ({c.pourcentage_benefices}%)</span>
                      <span className="font-semibold">{formatMoney(beneficeNet * (parseFloat(c.pourcentage_benefices) / 100))}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (optionnel)
                </label>
                <textarea
                  value={distributionData.description}
                  onChange={(e) => setDistributionData(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-green-500"
                  placeholder="Ex: Distribution mensuelle janvier 2026"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowDistributionModal(false); setDistributionData({ montant: '', description: '' }); }}
                  className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={beneficeNet <= 0}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  💰 Distribuer {formatMoney(beneficeNet)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectPayments;